import { secondAxios } from "../../../../../../api/axios";
import * as estimators from "./estimation";
import * as calculator from "./calculation";
import { estimateAll, materialData as materialDataConfig } from "./pvUtils";

const PV_DB_NAME = "pv";

export const QueryTypes = {
  SELECT: "SELECT",
  INSERT: "INSERT",
  UPDATE: "UPDATE",
  DELETE: "DELETE",
  RAW: "RAW",
};

export function quoteIdentifier(value) {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

export function quoteValue(value) {
  if (value === null || value === undefined) return "NULL";

  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }

  if (typeof value === "boolean") {
    return value ? "1" : "0";
  }

  return `'${String(value).replace(/'/g, "''")}'`;
}

function applyReplacements(sql, replacements = []) {
  let index = 0;

  return String(sql).replace(/\?/g, () => {
    const value = replacements[index];
    index += 1;
    return quoteValue(value);
  });
}

export async function queryPvDb(sql, options = {}) {
  if (!sql) {
    throw new Error("sql is required");
  }

  const finalSql = Array.isArray(options?.replacements)
    ? applyReplacements(sql, options.replacements)
    : String(sql);

  const res = await secondAxios.post(`/sqlite-db/query`, {
    dbName: PV_DB_NAME,
    sql: finalSql,
  });

  return res.data;
}

export async function getPvTable(tableName) {
  if (!tableName) {
    throw new Error("tableName is required");
  }

  const safeTableName = quoteIdentifier(tableName);

  const res = await secondAxios.post(`/sqlite-db/query`, {
    dbName: PV_DB_NAME,
    sql: `SELECT * FROM ${safeTableName}`,
  });

  return res.data;
}

export async function getPvTablesList() {
  return queryPvDb(
    `SELECT name FROM sqlite_master WHERE type='table' AND name NOT LIKE 'sqlite_%'`,
  );
}

async function dbAll(sql, params = []) {
  const finalSql = applyReplacements(sql, params);
  return queryPvDb(finalSql);
}

export async function estimationTable(input) {
  let stat = "";

  try {
    input = await estimateAll(input).catch(() => {
      stat = "failed estimateAll";
      throw new Error(stat);
    });

    stat = "estimate all done";
    console.log({ input });

    const [result, cost] = await estimators.EstimationSummary2(input).catch(
      () => {
        stat = "failed summary";
        throw new Error(stat);
      },
    );

    stat = "Compilation";

    return {
      data: result,
      cost,
    };
  } catch (err) {
    console.log(err);

    return {
      error: stat || err.message,
    };
  }
}

export async function estimateShell(inputs) {
  try {
    const response = await calculator.shellEstimation(inputs);

    const result = {
      DataGridView7: response[1],
      DataGridView5: response[0],
    };

    return {
      data: result,
    };
  } catch (err) {
    console.error("Error in pv-estimate-shell:", err);

    return {
      error:
        (err && err.message) ||
        (typeof err === "string" ? err : "Error doing calculation"),
    };
  }
}

export async function materialData(materials) {
  try {
    const promises = materialDataConfig.map(async (elem) => {
      const matName = materials[elem.materialFieldName];

      if (!matName) return;

      console.log("logging, debugging", elem.materialFieldName, matName);

      const stressRows = await dbAll(
        "SELECT `Allowable Stress (N/mm^2)` AS stress FROM `Material Allowable Stress` WHERE Material = ? LIMIT 1",
        [matName],
      );

      if (stressRows.length) {
        const stress = stressRows[0].stress;

        materials[elem.stressFieldName] = stress;

        elem?.stressPairfields?.forEach((pairField) => {
          materials[pairField] = stress;
        });
      }

      const curveRows = await dbAll(
        "SELECT `MDMT Curve 1` AS curve FROM `MDMT Curve 1` WHERE Material = ? LIMIT 1",
        [matName],
      );

      if (curveRows.length) {
        materials[elem.curveFieldName] = curveRows[0].curve;
      }
    });

    await Promise.all(promises);

    console.log("resp", materials);

    return {
      data: materials,
    };
  } catch (error) {
    console.error("An error occurred while running the queries:", error);

    return {
      error: "Failed to fetch material data",
    };
  }
}

export async function estimateNozzle(input) {
  try {
    console.log("we got input: ", { input });

    const resultShell = {};

    resultShell.Cylindrical = calculator.Cylindrical_Shell_Calc(input);
    resultShell.Elliptical = calculator.Elliptical_Head_Calc(input);

    const shellCalc = await calculator.Shell_Calc(input, resultShell);

    input.DataGridView6 = shellCalc;

    const DataGridView2 = await estimators.estimateNozzle(input);

    return {
      data: DataGridView2,
    };
  } catch (err) {
    console.log(err);

    return {
      error: "Failed to estimate nozzle",
    };
  }
}

export async function estimateSaddle(input) {
  try {
    const resultShell = {};

    resultShell.Cylindrical = calculator.Cylindrical_Shell_Calc(input);
    resultShell.Elliptical = calculator.Elliptical_Head_Calc(input);

    const shellCalc = await calculator.Shell_Calc(input, resultShell);

    input.DataGridView6 = shellCalc;

    const DataGridView13 = await estimators.estimateSaddle(input);

    return {
      data: DataGridView13,
    };
  } catch (err) {
    console.log(err);

    return {
      error: "Failed to estimate saddle",
    };
  }
}

export async function calculate(inputs) {
  try {
    const result = {};
    const Nozzle = inputs.Cmb_Nz_Mark || 0;

    result.Cylindrical = calculator.Cylindrical_Shell_Calc(inputs);
    result.Elliptical = calculator.Elliptical_Head_Calc(inputs);
    result.Nozzle = await calculator.Nozzle_Calc_1(inputs, Nozzle);
    result.Saddle = calculator.Saddle_Zick_Calc(inputs);
    result.Wind = calculator.Wind_Load(inputs);
    result.Seismic = calculator.Seismic_Load(inputs);
    result.Deflection = calculator.Deflection(inputs);
    result.StressMoment = calculator.Stress_Moment_Calc(inputs);
    result.Leg = calculator.Leg_Support_Calc(inputs);
    result.Lug = calculator.Lug_Support_Calc(inputs);
    result.Skirt = calculator.Skirt_Support(inputs);
    result.AnchorBolt = calculator.Anchor_Bolt(inputs);
    result.BaseRing = calculator.Base_Ring(inputs);
    result.Torispherical = calculator.Torispherical_Head_Calc(inputs);
    result.Spherical = calculator.Sphere_Hemispherical_Head_Calc(inputs);
    result.Flat = calculator.Flat_Head_Calc(inputs);
    result.Conical = calculator.Conical_Shell_Calc(inputs);

    return {
      data: result,
    };
  } catch (err) {
    console.log(err);

    return {
      error: "Failed to calculate",
    };
  }
}

export async function calculateShell(inputs) {
  try {
    const result = {};

    result.Cylindrical = calculator.Cylindrical_Shell_Calc(inputs);
    result.Elliptical = calculator.Elliptical_Head_Calc(inputs);

    const shellCalc = await calculator.Shell_Calc(inputs, result);

    result.DataGridView6 = shellCalc;

    return {
      data: result,
    };
  } catch (err) {
    console.log(err);

    return {
      error: "Error doing calculation",
    };
  }
}

export async function calculateNozzle(inputs) {
  try {
    const response = {
      DataGridView3: [],
    };

    const cylindricalCalc = calculator.Cylindrical_Shell_Calc(inputs);

    for (let i = 0; i < inputs.DataGridView1.length; i += 1) {
      let result;

      try {
        result = await calculator.Nozzle_Calc_1(inputs, i);
      } catch (e) {
        result = {
          txt_TRN_NC1: "Db Error",
          txt_AR_NC1: "Db Error",
          txt_AA_NC1: "Db Error",
          Actual_Weld_Size_A: "Db Error",
          Actual_Weld_Size_B: "Db Error",
          Actual_Weld_Size_C: "Db Error",
        };
      }

      response.DataGridView3.push({
        "Nozzle Thk Reqd": result.txt_TRN_NC1,
        "Area Reqd": result.txt_AR_NC1,
        "Area Available": result.txt_AA_NC1,
        "Nozzle Weld (A) Reqd": result.Actual_Weld_Size_A,
        "Nozzle Weld (B) Reqd": result.Actual_Weld_Size_B,
        "Nozzle Weld (C) Reqd": result.Actual_Weld_Size_C,

        "Flange MAWP": await calculator
          .getFlangeMAWP(
            inputs.DataGridView1[i]["Flange Rating"],
            inputs.txtDTemp,
          )
          .catch(() => "Db Error"),

        "Flange MDMT": await calculator
          .getMDMT(inputs.ComboBox13, cylindricalCalc.TextBox106)
          .catch(() => "Db Error"),

        "Pad MDMT": await calculator
          .getMDMT(inputs.ComboBox14, cylindricalCalc.TextBox106)
          .catch(() => "Db Error"),

        "Nozzle MDMT": await calculator
          .getMDMT(inputs.ComboBox12, cylindricalCalc.TextBox106)
          .catch(() => "Db Error"),
      });
    }

    return {
      data: response,
    };
  } catch (err) {
    console.log(err);

    return {
      error: "Failed to calculate nozzle",
    };
  }
}

const pvLegacyService = {
  queryPvDb,
  getPvTable,
  getPvTablesList,
  estimationTable,
  estimateShell,
  materialData,
  estimateNozzle,
  estimateSaddle,
  calculate,
  calculateShell,
  calculateNozzle,
};

export default pvLegacyService;